// Vertical-slice Iron Covenant units (bible-specified where stated; TEST VALUES otherwise).

import type { BossExtras, UnitDef } from './types';

export const UNITS: UnitDef[] = [
  {
    id: 'u-ic-worker',
    name: 'Covenant Worker',
    faction: 'iron-covenant',
    sizeTag: 'small',
    roleTags: ['worker'],
    squadMemberCount: 1,
    maxHealth: 60, // base ~48 + IC +25% worker durability
    armor: 'none',
    moveSpeed: 3.2,
    targetPriority: [],
    abilities: [],
    command: 0,
    captureWeight: 0,
    ui: { color: 0xd9c37e, scale: 0.55, label: 'Worker' },
  },
  {
    id: 'u-ic-vanguard',
    name: 'Vanguard Swordsmen',
    faction: 'iron-covenant',
    sizeTag: 'small',
    roleTags: ['frontline'],
    squadMemberCount: 6,
    maxHealth: 720, // bible IC-09
    armor: 'moderate',
    moveSpeed: 3.2,
    attack: { damage: 42, period: 1.0, range: 1.6 },
    targetPriority: ['squad', 'worker', 'building'],
    abilities: ['ab-lock-shields', 'ab-finish-the-line'],
    command: 3,
    captureWeight: 1,
    ui: { color: 0x8fa8c8, scale: 0.85, label: 'Vanguard' },
  },
  {
    id: 'u-ic-shield-legion',
    name: 'Shield Legion',
    faction: 'iron-covenant',
    sizeTag: 'small',
    roleTags: ['frontline'],
    squadMemberCount: 6,
    maxHealth: 780, // TEST
    armor: 'heavy',
    moveSpeed: 3.0,
    attack: { damage: 26, period: 1.0, range: 1.6 },
    targetPriority: ['squad', 'worker', 'building'],
    abilities: ['ab-brace'],
    command: 3,
    captureWeight: 1,
    // hanging shield screens on the Crown Colossus (bible IC-25)
    mountableOn: ['boss-ic-crown-colossus'],
    mountEffect: { stat: 'rangedDamageTaken', mult: 0.85 },
    ui: { color: 0x6f87b8, scale: 0.9, label: 'Legion' },
  },
  {
    id: 'u-ic-pike-wardens',
    name: 'Pike Wardens',
    faction: 'iron-covenant',
    sizeTag: 'small',
    roleTags: ['anti-large', 'frontline'],
    squadMemberCount: 6,
    maxHealth: 700, // TEST
    armor: 'moderate',
    moveSpeed: 3.2,
    attack: {
      damage: 34,
      period: 1.0,
      range: 2.2,
      damageVs: { large: 1.6, giant: 2.2 }, // TEST — giant-killer bump
    },
    targetPriority: ['giant', 'large', 'squad', 'building'],
    abilities: ['ab-impaling-hedge', 'ab-giant-mark'],
    command: 3,
    captureWeight: 1,
    ui: { color: 0xb8985f, scale: 0.9, label: 'Pikes' },
  },
  {
    id: 'u-ic-mobile-ballista',
    name: 'Mobile Ballista',
    faction: 'iron-covenant',
    sizeTag: 'large',
    roleTags: ['siege', 'ranged'],
    squadMemberCount: 3, // ballista + two crew (render)
    maxHealth: 900, // TEST
    armor: 'light',
    moveSpeed: 2.4,
    attack: {
      damage: 130,
      period: 2.6,
      range: 16,
      projectileSpeed: 22,
      damageVs: { giant: 1.6, large: 1.2 }, // TEST — giant-killer bump
      structureMult: 1.5,
    },
    targetPriority: ['giant', 'siege', 'building', 'trap', 'squad'],
    abilities: [],
    command: 6,
    captureWeight: 0.5,
    ui: { color: 0xc8c0a8, scale: 1.2, label: 'Ballista' },
  },
  {
    id: 'u-ic-longshot-snipers',
    name: 'Longshot Snipers',
    faction: 'iron-covenant',
    sizeTag: 'small',
    roleTags: ['ranged'],
    squadMemberCount: 4, // bible IC-13: four precision riflemen
    maxHealth: 520, // TEST
    armor: 'light',
    moveSpeed: 3.0,
    attack: {
      damage: 58,
      period: 1.8,
      range: 14,
      projectileSpeed: 34,
      damageVs: { large: 1.2, giant: 1.2 },
    },
    targetPriority: ['worker', 'squad', 'siege', 'large'],
    abilities: [],
    command: 4,
    captureWeight: 0.5,
    // crown-platform gunners on the Colossus (bible IC-25)
    mountableOn: ['boss-ic-crown-colossus'],
    ui: { color: 0x9aa87e, scale: 0.85, label: 'Snipers' },
  },
  {
    id: 'u-ic-ironclad-tank',
    name: 'Ironclad Assault Tank',
    faction: 'iron-covenant',
    sizeTag: 'large',
    roleTags: ['siege', 'frontline'],
    squadMemberCount: 1,
    maxHealth: 1850, // bible IC-14
    armor: 'heavy',
    moveSpeed: 2.2,
    attack: {
      damage: 105,
      period: 1.8,
      range: 9,
      projectileSpeed: 26,
      damageVs: { large: 1.15 },
      structureMult: 1.4, // Breach Round flavor folded into the base attack
    },
    targetPriority: ['building', 'trap', 'siege', 'squad'],
    abilities: [],
    command: 8,
    captureWeight: 0.5,
    ui: { color: 0x7e8894, scale: 1.35, label: 'Ironclad' },
  },
  {
    id: 'boss-ic-arcturus',
    name: 'Siege Captain Arcturus',
    faction: 'iron-covenant',
    sizeTag: 'large',
    roleTags: ['boss', 'siege'],
    squadMemberCount: 1,
    maxHealth: 825, // halved from previous prototype value per owner feedback
    armor: 'heavy',
    moveSpeed: 2.6,
    attack: {
      damage: 95,
      period: 1.5,
      range: 2.5,
      structureMult: 1.6, // Breach Order: 160% structure damage
    },
    targetPriority: ['building', 'squad'],
    abilities: ['ab-captains-guard'],
    command: 7,
    isBoss: true,
    captureWeight: 0.5,
    ui: { color: 0xd8b04a, scale: 1.7, label: 'Arcturus' },
  },
  {
    id: 'u-ic-banner-captain',
    name: 'Banner Captain',
    faction: 'iron-covenant',
    sizeTag: 'small',
    roleTags: ['support'],
    squadMemberCount: 4, // bible IC-12: a captain and three honor guards
    maxHealth: 560, // TEST
    armor: 'moderate',
    moveSpeed: 3.2,
    attack: { damage: 22, period: 1.2, range: 1.8 },
    targetPriority: ['squad', 'building'],
    abilities: ['ab-unbroken-standard', 'ab-rally-the-fallen'],
    command: 4,
    captureWeight: 0.5,
    maxActiveUnits: 2, // bible IC-12: two globally
    ui: { color: 0xd8c07a, scale: 0.9, label: 'Banner' },
  },
  {
    id: 'u-ic-master-builder',
    name: 'Master Builder',
    faction: 'iron-covenant',
    sizeTag: 'small',
    roleTags: ['worker', 'builder'],
    squadMemberCount: 1,
    maxHealth: 96, // bible IC-15: +60% over the 60-health Covenant worker
    armor: 'light',
    moveSpeed: 3.2,
    targetPriority: [],
    abilities: [],
    command: 0,
    captureWeight: 0,
    maxActiveUnits: 4, // bible IC-15
    workerVariant: { buildMult: 1.45, repairMult: 1.25, carryMult: 1.2 },
    ui: { color: 0xe8d090, scale: 0.65, label: 'Master Builder' },
  },
  {
    id: 'boss-ic-iron-saint',
    name: 'The Iron Saint',
    faction: 'iron-covenant',
    sizeTag: 'large',
    roleTags: ['boss', 'frontline'],
    squadMemberCount: 1,
    maxHealth: 1900, // halved from previous prototype value per owner feedback
    armor: 'very-heavy',
    moveSpeed: 2.4,
    attack: { damage: 85, period: 1.6, range: 2.5, structureMult: 1.2 },
    targetPriority: ['squad', 'building'],
    abilities: ['ab-saints-rebuke'],
    command: 12,
    isBoss: true,
    captureWeight: 0.5,
    ui: { color: 0xcfd6e4, scale: 1.9, label: 'Iron Saint' },
  },
  {
    id: 'boss-ic-crown-colossus',
    name: 'Crown Colossus',
    faction: 'iron-covenant',
    sizeTag: 'giant',
    roleTags: ['boss', 'siege'],
    squadMemberCount: 1,
    maxHealth: 2900, // halved from previous prototype value per owner feedback
    armor: 'heavy',
    moveSpeed: 1.6,
    attack: { damage: 160, period: 2.2, range: 3.5, structureMult: 1.8 },
    targetPriority: ['building', 'squad'],
    abilities: ['ab-kingbreaker-hammer', 'ab-ic-colossus-crush'],
    command: 20,
    isBoss: true,
    captureWeight: 0.5,
    mountSlots: 2, // bible IC-25: two attachments
    ui: { color: 0xd8b04a, scale: 2.6, label: 'Colossus' },
  },

  // ------------------------------------------------- v0.2.4 roster completion
  {
    id: 'u-ic-crossbow-retinue',
    name: 'Crossbow Retinue',
    faction: 'iron-covenant',
    sizeTag: 'small',
    roleTags: ['ranged'],
    squadMemberCount: 5, // bible IC-03: five crossbowmen
    maxHealth: 540, // TEST
    armor: 'light',
    moveSpeed: 3.0,
    attack: { damage: 30, period: 1.3, range: 9, projectileSpeed: 26 }, // TEST
    targetPriority: ['squad', 'worker', 'siege'],
    abilities: ['ab-pavise-stakes', 'ab-focused-bolts'],
    command: 3,
    captureWeight: 0.5,
    ui: { color: 0xa39b7a, scale: 0.85, label: 'Crossbows' },
  },
  {
    // bible IC-04. Surveyor's Eye (building/trap reveal) has no vision/intel system to
    // hang on — folded into the Scouts' speed and worker-first targeting (deviation).
    // Harassing Volley (fire on the move, reduced vs frontline) is folded into a light
    // ranged attack with worker priority (deviation).
    id: 'u-ic-royal-scouts',
    name: 'Royal Scouts',
    faction: 'iron-covenant',
    sizeTag: 'medium',
    roleTags: ['raider', 'ranged'],
    squadMemberCount: 4, // bible IC-04: four light horsemen
    maxHealth: 440, // TEST
    armor: 'light',
    moveSpeed: 4.2, // TEST — fastest Covenant squad (site races, raids)
    attack: { damage: 24, period: 1.1, range: 7, projectileSpeed: 26 }, // TEST
    targetPriority: ['worker', 'squad', 'siege'],
    abilities: [],
    command: 2,
    captureWeight: 1,
    ui: { color: 0xc9b168, scale: 0.9, label: 'Scouts' },
  },
  {
    id: 'u-ic-forge-clerics',
    name: 'Forge Clerics',
    faction: 'iron-covenant',
    sizeTag: 'small',
    roleTags: ['support'],
    squadMemberCount: 3, // bible IC-08: three armored clerics
    maxHealth: 480, // TEST
    armor: 'moderate',
    moveSpeed: 3.0,
    attack: { damage: 18, period: 1.3, range: 1.8 }, // TEST — hammer censers
    targetPriority: ['squad'],
    abilities: ['ab-tempered-blessing', 'ab-field-mend'],
    command: 3,
    captureWeight: 0.5,
    maxActiveUnits: 3, // bible IC-08: maximum three active
    ui: { color: 0xd88f4a, scale: 0.85, label: 'Clerics' },
  },
  {
    id: 'u-ic-iron-cavalry',
    name: 'Iron Cavalry',
    faction: 'iron-covenant',
    sizeTag: 'medium',
    roleTags: ['raider', 'frontline'],
    squadMemberCount: 5, // bible IC-09: five plated riders
    maxHealth: 760, // TEST
    armor: 'heavy',
    moveSpeed: 4.4, // TEST
    attack: { damage: 46, period: 1.1, range: 1.8 }, // TEST
    targetPriority: ['squad', 'worker', 'siege'],
    abilities: ['ab-breakthrough-charge', 'ab-wheel-and-reform'],
    command: 5,
    captureWeight: 1,
    ui: { color: 0x7a92a8, scale: 1.0, label: 'Cavalry' },
  },
  {
    id: 'u-ic-oathbreaker-hunters',
    name: 'Oathbreaker Hunters',
    faction: 'iron-covenant',
    sizeTag: 'small',
    roleTags: ['ranged'],
    squadMemberCount: 4, // bible IC-12: four masked hunters
    maxHealth: 560, // TEST
    armor: 'moderate',
    moveSpeed: 3.4,
    attack: { damage: 40, period: 1.3, range: 8, projectileSpeed: 26 }, // TEST — chain launchers
    targetPriority: ['squad', 'worker'],
    abilities: ['ab-writ-of-condemnation', 'ab-execution-chains'],
    command: 4,
    captureWeight: 0.5,
    ui: { color: 0x8a7a94, scale: 0.9, label: 'Hunters' },
  },
  {
    id: 'u-ic-royal-artificers',
    name: 'Royal Artificers',
    faction: 'iron-covenant',
    sizeTag: 'small',
    roleTags: ['support'],
    squadMemberCount: 3, // bible IC-13: three master engineers
    maxHealth: 460, // TEST
    armor: 'light',
    moveSpeed: 3.0,
    attack: { damage: 14, period: 1.5, range: 6, projectileSpeed: 22 }, // TEST — tool bolts
    targetPriority: ['squad'],
    abilities: ['ab-aegis-drone', 'ab-overcharge-engine'],
    command: 4,
    captureWeight: 0.5,
    maxActiveUnits: 2, // bible IC-13: maximum two active
    ui: { color: 0xc8a878, scale: 0.85, label: 'Artificers' },
  },
  {
    id: 'u-ic-bastion-golems',
    name: 'Bastion Golems',
    faction: 'iron-covenant',
    sizeTag: 'large',
    roleTags: ['frontline'],
    squadMemberCount: 2, // bible IC-14: construct pair
    maxHealth: 1700, // TEST
    armor: 'very-heavy',
    moveSpeed: 1.9, // TEST — deliberately ponderous
    attack: { damage: 60, period: 1.7, range: 2.2 }, // TEST
    targetPriority: ['squad', 'building'],
    abilities: ['ab-stonewall-stance', 'ab-retaliation-bell'],
    command: 6,
    captureWeight: 0.5,
    ui: { color: 0x8c8478, scale: 1.4, label: 'Golems' },
  },
  {
    // bible IC-15. No flight mechanic in the sim — implemented as a fast ground squad
    // whose dives/extractions are approximated by abilities (Canopy Riders precedent).
    id: 'u-ic-gryphon-lancers',
    name: 'Gryphon Lancers',
    faction: 'iron-covenant',
    sizeTag: 'large',
    roleTags: ['raider'],
    squadMemberCount: 3, // bible IC-15: three armored gryphon riders
    maxHealth: 920, // TEST
    armor: 'moderate',
    moveSpeed: 4.6, // TEST — approximates flight over terrain
    attack: { damage: 55, period: 1.2, range: 2.0 }, // TEST — dive lances
    targetPriority: ['worker', 'squad', 'siege'],
    abilities: ['ab-skyhook-dive', 'ab-rescue-lift', 'ab-cavalry-charge'],
    command: 6,
    captureWeight: 0.5,
    ui: { color: 0xe8dcb0, scale: 1.25, label: 'Gryphons' },
  },
  {
    id: 'u-ic-fortress-ram',
    name: 'Fortress Ram',
    faction: 'iron-covenant',
    sizeTag: 'giant',
    roleTags: ['siege', 'frontline'],
    squadMemberCount: 1,
    maxHealth: 2400, // TEST — giant HP pass: −25% so focused fire pays off
    armor: 'very-heavy',
    moveSpeed: 1.6,
    attack: { damage: 150, period: 2.4, range: 2.5, structureMult: 2.0 }, // TEST — exists to breach
    targetPriority: ['building', 'trap', 'squad'],
    abilities: ['ab-covered-advance', 'ab-gatebreaker', 'ab-ic-ram-charge'],
    command: 9,
    captureWeight: 0.5,
    ui: { color: 0x6a6258, scale: 2.2, label: 'Fortress Ram' },
  },
  {
    // bible IC-17. Royal Challenge's +20% vs the strongest non-Giant is partly folded
    // into damageVs.large (deviation); the -20% vs other targets is dropped.
    id: 'u-ic-kings-exemplars',
    name: "King's Exemplars",
    faction: 'iron-covenant',
    sizeTag: 'small',
    roleTags: ['frontline'],
    squadMemberCount: 5, // bible IC-17: five legendary champions
    maxHealth: 1050, // TEST
    armor: 'heavy',
    moveSpeed: 3.2,
    attack: { damage: 58, period: 1.0, range: 1.8, damageVs: { large: 1.2 } }, // TEST
    targetPriority: ['large', 'squad', 'building'],
    abilities: ['ab-royal-challenge', 'ab-last-banner'],
    command: 7,
    captureWeight: 1,
    ui: { color: 0xf0d060, scale: 0.95, label: 'Exemplars' },
  },
  {
    // Core Annex System (owner spec 2026-07-13): free Reserve Cohort prepared by the
    // High Command Hall. Costs 2 Command despite costing no resources; competent
    // defender but weaker than a deck squad; auto-Defends the Core rally point.
    id: 'u-ic-reserve-cohort',
    name: 'Reserve Cohort',
    faction: 'iron-covenant',
    sizeTag: 'small',
    roleTags: ['frontline'],
    squadMemberCount: 4, // spec: a small four-soldier shield squad
    maxHealth: 380, // TEST — deliberately below Shield Legion's pro-rata durability
    armor: 'moderate',
    moveSpeed: 3.0,
    attack: { damage: 20, period: 1.1, range: 1.6 }, // TEST
    targetPriority: ['squad', 'worker', 'large'],
    abilities: [],
    command: 2, // spec
    captureWeight: 0.5,
    ui: { color: 0x8494aa, scale: 0.85, label: 'Reserve Cohort' },
  },
  {
    // Core Annex System: armored cart pulled by mechanical oxen, produced by the
    // Marching Foundry. E: Establish Field Bastion locks it into the ground.
    id: 'u-ic-bastion-wagon',
    name: 'Bastion Wagon',
    faction: 'iron-covenant',
    sizeTag: 'large',
    roleTags: ['siege', 'support'],
    squadMemberCount: 1,
    maxHealth: 1250, // TEST — heavily armored cart
    armor: 'heavy',
    moveSpeed: 1.5, // TEST — moves slowly
    targetPriority: [],
    abilities: [],
    command: 0, // deployable building-carrier, not an army unit
    captureWeight: 0,
    deploysInto: {
      buildingId: 'b-ic-field-bastion',
      assembleSeconds: 60, // spec: automatically builds for 60 s
      maxDeployed: 1, // spec: maximum one active Field Bastion from this annex
      witherSeconds: 12, // TEST — replaced bastion collapses over 12 s
      label: 'Establish Field Bastion',
    },
    ui: { color: 0x7d8aa0, scale: 1.4, label: 'Bastion Wagon' },
  },
];

export const BOSS_EXTRAS: Record<string, BossExtras> = {
  'boss-ic-arcturus': {
    escortAttackSpeedPer: 0.05,
    escortAttackSpeedCap: 0.2,
    summonSeconds: 15, // owner request 2026-07-13
    announcement: 'local',
  },
  'boss-ic-iron-saint': {
    summonSeconds: 30, // owner request 2026-07-13
    announcement: 'global',
    // Procession of Arms (bible IC-24)
    escortClassBonuses: {
      infantryDamageTakenMult: 0.85,
      rangedAttackSpeedMult: 1.1, // shoulder reliquaries approximated as attack tempo
      siegeStructureDamageMult: 1.2,
      supportRegenPerSecond: 12,
    },
  },
  'boss-ic-crown-colossus': {
    summonSeconds: 60, // owner request 2026-07-13
    announcement: 'global',
  },
};
